import PropTypes from 'prop-types';
import React, { useRef, useState, useEffect } from 'react';

import { mediumUp, largeUp } from 'constants/mediaQueries';
import Theme from 'styles/theme';

const SearchPageSentinel = ({ showSentinel, handleSentinelEvent }) => {
  const sentinelRef = useRef(null);
  const isObserverInitialCallRef = useRef(true);

  const [showSpinnerState, setShowSpinnerState] = useState(false);

  const handleSentinelIntersection = entries => {
    entries.forEach(entry => {
      if (isObserverInitialCallRef.current) {
        isObserverInitialCallRef.current = false;

        return;
      }

      if (entry.isIntersecting) {
        setShowSpinnerState(true);
        handleSentinelEvent();
      } else {
        setShowSpinnerState(false);
      }
    });
  };

  useEffect(() => {
    if (showSentinel) {
      const observer = new window.IntersectionObserver(handleSentinelIntersection);
      observer.observe(sentinelRef.current);

      return () => {
        observer.disconnect();

        isObserverInitialCallRef.current = true;
      };
    }

    setShowSpinnerState(showSentinel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSentinel]);

  return (
    <div className="SearchPageSentinel">
      {showSentinel && (
        <div className="SearchPageSentinel_sentinel" aria-hidden ref={sentinelRef} />
      )}

      {showSpinnerState && (
        <div className="SearchPageSentinel__spinner-container">
          <div className="SearchPageSentinel__spinner" />
        </div>
      )}

      <style jsx>
        {`
          .SearchPageSentinel {
            width: 100%;
          }
        `}
      </style>

      <style jsx global>
        {`
          .SearchPageSentinel .SearchPageSentinel_sentinel {
            height: 0;
            width: 0;
            overflow: hidden;
            visibility: hidden;
          }

          .SearchPageSentinel .SearchPageSentinel__spinner-container {
            padding-top: ${Theme.get('spacing') * 10}px;
            padding-bottom: ${Theme.get('spacing') * 1.5}px;
          }

          .SearchPageSentinel .SearchPageSentinel__spinner {
            width: ${Theme.get('spacing') * 10}px;
            aspect-ratio: 1;

            border: ${Theme.get('spacing')}px solid ${Theme.get('colors-neutral-20')};
            border-top-color: ${Theme.get('colors-black-transparent')};
            border-radius: 50%;

            animation: spin 1s linear infinite;
            margin: auto;
          }

          @media ${mediumUp} {
            .SearchPageSentinel .SearchPageSentinel__spinner-container {
              padding-top: ${Theme.get('spacing') * 3}px;
              padding-bottom: 0;
            }
          }

          @media ${largeUp} {
            .SearchPageSentinel .SearchPageSentinel__spinner {
              width: ${Theme.get('spacing') * 12}px;
              position: absolute;
              left: 0;
              right: 0;
            }
          }

          @keyframes spin {
            from {
              transform: rotate(0);
            }
            to {
              transform: rotate(1turn);
            }
          }
        `}
      </style>
    </div>
  );
};

SearchPageSentinel.propTypes = {
  showSentinel: PropTypes.bool,
  handleSentinelEvent: PropTypes.func.isRequired,
};

SearchPageSentinel.defaultProps = {
  showSentinel: false,
};

export { SearchPageSentinel };
